import { useState } from 'react';
import { useToast } from '@/shared/components/ui/Toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { ConfirmDialog } from './ConfirmDialog';
import { useAppSettings, useToggleFeatureFlag } from '../hooks/useAdmin';
import { framesService } from '@/features/frames-to-order/services/framesService';

export function FeatureFlagsTab() {
  const { showToast } = useToast();
  const { data: settings, isLoading } = useAppSettings();
  const toggleMutation = useToggleFeatureFlag();
  const [showMarkAllDialog, setShowMarkAllDialog] = useState(false);
  const [markAllPoNumber, setMarkAllPoNumber] = useState('');
  const [markAllOrderedDate, setMarkAllOrderedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const handleToggle = async (key: string, currentValue: boolean) => {
    try {
      await toggleMutation.mutateAsync({
        key,
        enabled: !currentValue,
      });
      showToast(
        `Feature ${!currentValue ? 'enabled' : 'disabled'} successfully`,
        'success'
      );
    } catch (error) {
      console.error('Error toggling feature:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to toggle feature',
        'error'
      );
    }
  };

  const handleMarkAllAsOrdered = async () => {
    try {
      setIsMarkingAll(true);

      // Get all PENDING frames
      const pendingFrames = await framesService.getFrames({ status: ['PENDING'] });

      if (pendingFrames.length === 0) {
        showToast('No pending frames found to mark as ordered', 'info');
        setShowMarkAllDialog(false);
        return;
      }

      // Mark all as ordered
      const frameIds = pendingFrames.map((f) => f.id);
      await framesService.markAsOrdered({
        frame_ids: frameIds,
        po_number: markAllPoNumber.trim() || '',
        ordered_date: markAllOrderedDate,
      });

      showToast(
        `Successfully marked ${frameIds.length} frame(s) as ordered`,
        'success'
      );
      setShowMarkAllDialog(false);
      setMarkAllPoNumber('');
      setMarkAllOrderedDate(new Date().toISOString().split('T')[0]);
    } catch (error) {
      console.error('Error marking frames as ordered:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to mark frames as ordered',
        'error'
      );
    } finally {
      setIsMarkingAll(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading settings...</p>
      </div>
    );
  }

  // Parse settings from database format
  type Setting = { setting_key: string; setting_value: string; description?: string | null };
  const allSettings: Setting[] = settings || [];
  const featureFlags = allSettings.filter((s) => s.setting_key.startsWith('feature_'));
  const reportSettings = allSettings.filter((s) => s.setting_key.startsWith('report_visible_'));

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Feature Flags & Reports
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Control which features and reports are visible to users
        </p>
      </div>

      <div className="space-y-6">
        {/* Feature Flags */}
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Feature Flags
          </h3>
          <div className="space-y-4">
            {featureFlags.length > 0 ? (
              featureFlags.map((flag: Setting) => {
                const isEnabled = flag.setting_value === 'true';
                const displayName = flag.setting_key
                  .replace('feature_', '')
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (l: string) => l.toUpperCase());

                return (
                  <div
                    key={flag.setting_key}
                    className="flex items-center justify-between py-3 border-b last:border-b-0"
                  >
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {displayName}
                      </h4>
                      {flag.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {flag.description}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleToggle(flag.setting_key, isEnabled)}
                      disabled={toggleMutation.isPending}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${isEnabled ? 'bg-blue-600' : 'bg-gray-200'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${isEnabled ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">
                No feature flags configured
              </p>
            )}
          </div>
        </div>

        {/* Report Visibility */}
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Report Visibility
          </h3>
          <div className="space-y-4">
            {reportSettings.length > 0 ? (
              reportSettings.map((report: Setting) => {
                const isVisible = report.setting_value === 'true';
                const displayName = report.setting_key
                  .replace('report_visible_', '')
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (l: string) => l.toUpperCase());

                return (
                  <div
                    key={report.setting_key}
                    className="flex items-center justify-between py-3 border-b last:border-b-0"
                  >
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {displayName}
                      </h4>
                      {report.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {report.description}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleToggle(report.setting_key, isVisible)}
                      disabled={toggleMutation.isPending}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${isVisible ? 'bg-blue-600' : 'bg-gray-200'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${isVisible ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">
                No report settings configured
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Data Operations */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Data Operations
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Bulk operations for managing frame data across all locations
        </p>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                Mark All Frames as Ordered
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Mark all PENDING frames across all locations as ORDERED
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowMarkAllDialog(true)}
            >
              Execute
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          About Feature Flags & Reports
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>
            Feature flags control which application features are enabled for all
            users
          </li>
          <li>
            Report visibility controls which reports appear in the Reports menu
          </li>
          <li>Changes take effect immediately for all users</li>
          <li>
            Settings are stored in the app_settings table and can be managed via
            the database
          </li>
        </ul>
      </div>

      {/* Mark All as Ordered Dialog */}
      <ConfirmDialog
        isOpen={showMarkAllDialog}
        onClose={() => {
          setShowMarkAllDialog(false);
          setMarkAllPoNumber('');
          setMarkAllOrderedDate(new Date().toISOString().split('T')[0]);
        }}
        onConfirm={handleMarkAllAsOrdered}
        title="Mark All Frames as Ordered"
        confirmLabel="Mark All as Ordered"
        confirmVariant="primary"
        isLoading={isMarkingAll}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will mark all PENDING frames across all locations as ORDERED with the
            following details:
          </p>
          <Input
            label="PO Number (Optional)"
            value={markAllPoNumber}
            onChange={(e) => setMarkAllPoNumber(e.target.value)}
            placeholder="Enter PO number"
          />
          <Input
            label="Ordered Date"
            type="date"
            value={markAllOrderedDate}
            onChange={(e) => setMarkAllOrderedDate(e.target.value)}
            required
          />
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This action cannot be undone. All pending frames
              will be marked as ordered.
            </p>
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
